import { IconButton, Stack, Typography, useTheme } from "@mui/material";
import { PinFilledIcon, Tooltip } from "components";
import { Button } from "components/button";
import { memo } from "react";
import type { PinnedMessageItem } from "shared/types/chat";

interface PinnedMessageBannerProps {
  pinnedMessages: PinnedMessageItem[];
  onViewAllClick: () => void;
  onUnpin: (chatId: string) => void;
  onPinnedMessageClick: (chatId: string) => void;
}

export const PinnedMessageBanner = memo(function PinnedMessageBanner({
  pinnedMessages,
  onViewAllClick,
  onUnpin,
  onPinnedMessageClick,
}: PinnedMessageBannerProps) {
  const theme = useTheme();

  if (pinnedMessages.length === 0) return null;

  const latestPinned = pinnedMessages[0];
  let previewText = latestPinned.excerpt ?? "";
  if (!previewText && latestPinned.chat?.attachments?.length > 0) {
    const firstName = latestPinned.chat.attachments[0].file_name;
    previewText =
      latestPinned.chat.attachments.length > 1
        ? `${firstName} +${latestPinned.chat.attachments.length - 1} more`
        : firstName;
  }
  if (previewText.length > 120) {
    previewText = `${previewText.slice(0, 120)}...`;
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="8px"
      sx={{
        padding: "8px 24px",
        height: "40px",
        boxShadow: "0px 0px 4px 0px #0000000F",
        "& .pin-view-all": {
          opacity: 0,
          transition: "opacity 0.2s ease",
        },
        "&:hover": {
          backgroundColor: theme.palette.neutral["005"],
        },
        "&:hover .pin-view-all": {
          opacity: 1,
        },
      }}
    >
      <Tooltip title="Unpin">
        <IconButton
          aria-label="Unpin"
          onClick={() => onUnpin(latestPinned.chat_id)}
          sx={{ p: 0, flexShrink: 0 }}
        >
          <PinFilledIcon
            htmlColor={theme.palette.primary.main}
            sx={{ fontSize: 20 }}
          />
        </IconButton>
      </Tooltip>
      <Stack
        sx={{
          height: "16px",
          backgroundColor: theme.palette.neutral["015"],
          borderRadius: "100%",
          aspectRatio: 1,
          justifyContent: "center",
          alignItems: "center",
        }}
        direction="row"
      >
        <Typography variant="caption-xs" color={theme.palette.lightBg.medium}>
          {pinnedMessages.length}
        </Typography>
      </Stack>
      <Typography
        variant="body-small"
        onClick={() => onPinnedMessageClick(latestPinned.chat_id)}
        sx={{
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          cursor: "pointer",
          color: theme.palette.lightBg.medium,
        }}
      >
        {previewText}
      </Typography>
      {pinnedMessages.length > 1 && (
        <Button
          className="pin-view-all"
          variant="ghost"
          isLink
          onClick={onViewAllClick}
        >
          View all
        </Button>
      )}
    </Stack>
  );
});
